// src/pages/UpdatePassword.tsx
import { Row, Col, Card, Form, Button, Image, Alert } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { login as apiLogin } from "services/auth";
import { useAuth } from "context/AuthContext";
// import authApi from "services/authApi"; // axios instance: baseURL, withCredentials, CSRF interceptor
import {PasswordField} from "services/passViewer";
import http from "services/httpClient";
import { getTimezoneOptions } from "utils/timezones";
import { DateTime } from "luxon";

function getEmailFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const decoded = jwtDecode<{ email?: string; sub?: string }>(token);
    return decoded.email ?? decoded.sub ?? null;
  } catch {
    return null;
  }
}

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{12,128}$/;

function checkPassword(pw: string) {
  return {
    length: pw.length >= 12 && pw.length <= 128,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^\w\s]/.test(pw),
    valid: PASSWORD_REGEX.test(pw),
  };
}

const UpdatePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const emailFromToken = getEmailFromToken(token);
  const { handleLogin } = useAuth();
  const timezones = useMemo(() => getTimezoneOptions(DateTime.now()), []);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    cellphone: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const submitted = useRef(false); // guard double-submit in React StrictMode

  const [touched] = useState({ password: false, confirm: false });

  const pw = formData.password;
  const pw2 = formData.confirmPassword;
  const rules = checkPassword(pw);
  const match = pw && pw2 && pw === pw2;

  // const timezones = [
  //   { label: "UTC", value: "UTC" },
  //   { label: "Asia/Manila (GMT+08:00)", value: "Asia/Manila" },
  //   { label: "Australia/Sydney (GMT+10:00)", value: "Australia/Sydney" },
  //   { label: "Australia/Brisbane (GMT+10:00)", value: "Australia/Brisbane" },
  //   { label: "Australia/Perth (GMT+08:00)", value: "Australia/Perth" },
  //   { label: "Australia/Adelaide (GMT+09:30)", value: "Australia/Adelaide" },
  // ];

  // at the top of the component file
  type FormEl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

  const handleChange: React.ChangeEventHandler<FormEl> = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitted.current || loading) return;
    submitted.current = true;
    setError(null);
    setSuccess(null);

    if (!token) {
      setError("Missing or invalid verification token.");
      submitted.current = false;
      return;
    }
    if (!emailFromToken) {
      setError("Cannot read email from token. Ask admin to resend the invite.");
      submitted.current = false;
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      submitted.current = false;
      return;
    }

    setLoading(true);
    try {
      // 1) Set password using verification token
      const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // harmless here; auth not required
        body: JSON.stringify({ token, new_password: formData.password }),
      });
      if (!res.ok) throw new Error(await res.text());

      // 2) Cookie login (sets access cookie) + attach refresh (sets refresh cookie)
      await apiLogin(emailFromToken, formData.password);

      // 3) Update AuthContext by fetching current user via cookies
      await handleLogin();

      // 4) Create/update profile (uses axios withCredentials + CSRF header)
      await http.post(`/user/profile/`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        cellphone: formData.cellphone,
        timezone: formData.timezone,
      });
      setTimeout(() => {
        setSuccess(null);
        navigate("/");
        window.location.href = "/";
      }, 3000);
    } catch (err) {
      // console.error(err);
      setError("Failed to update password. Please try again.");
    } finally {
      setLoading(false);
      setTimeout(() => (submitted.current = false), 300); // allow another submit after a moment
    }
  };

  return (
    <Row className="align-items-center justify-content-center g-0 min-vh-100">
      <Col xxl={6} lg={8} md={10} xs={12} className="py-8 py-xl-0">
        <Card className="smooth-shadow-md">
          <Card.Body className="p-6">
            <div className="mb-4">
              <Image src="/images/brand/logo/dustech_logo.webp" className="mb-2" alt="" />
              <p className="mb-6">Set your new password.</p>
            </div>

            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form.Group className="mb-3" controlId="firstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  placeholder="John"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="lastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  placeholder="Doe"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="cellphone">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  name="cellphone"
                  placeholder="e.g. +61412345678"
                  value={formData.cellphone}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="timezone">
                <Form.Label>Timezone</Form.Label>
                <Form.Select name="timezone" value={formData.timezone} onChange={handleChange} required>
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <PasswordField
                    id="password"
                    label="Password"
                    name="password"
                    value={pw}
                    onChange={handleChange}
                    onBlur={() => (touched.password = true)}     // 👈 mark touched
                    required
                    isInvalid={touched.password && !rules.valid}
                    feedbackInvalid="Please meet all password requirements."
                />
                <Form.Text className="text-muted">
                    Must be 12+ chars, include upper, lower, number, and symbol.
                </Form.Text>
                <ul className="small mt-2 mb-3">
                    <li className={rules.length ? "text-success" : "text-danger"}>12–128 characters</li>
                    <li className={rules.upper ? "text-success" : "text-danger"}>At least one uppercase letter</li>
                    <li className={rules.lower ? "text-success" : "text-danger"}>At least one lowercase letter</li>
                    <li className={rules.digit ? "text-success" : "text-danger"}>At least one number</li>
                    <li className={rules.symbol ? "text-success" : "text-danger"}>At least one symbol</li>
                </ul>

                {/* Confirm Password */}
                <PasswordField
                    id="confirmPassword"
                    label="Confirm Password"
                    name="confirmPassword"
                    value={pw2}
                    onChange={handleChange}
                    onBlur={() => (touched.confirm = true)} // 👈 mark touched
                    required
                    isInvalid={touched.confirm && !!pw2 && !match}
                    feedbackInvalid="Passwords do not match."
                />

              

              <div className="d-grid">
                <Button 
                  variant="primary" 
                  type="submit" 
                   disabled={loading || !rules.valid || !match}
                >
                  {loading ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default UpdatePassword;